import { ValueGetterFunc } from "@ag-grid-community/core";
import { Button } from "@chakra-ui/react";
import Decimal from "decimal.js";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import {
  getFundAllocationColumnData,
  getFundAllocationForFeeOffsets,
} from "features/fees/post/apply-offsets/FeePostApplyOffsetsTableUtils";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useFundFamilyConfigRetrieveSuspense } from "gen/endpoints/fund-family-config/fund-family-config";
import { FeeChargeEventPageData } from "gen/model/feeChargeEventPageData";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { FundFamilyUseAllocationTableDef } from "gen/model/fundFamilyUseAllocationTableDef";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FundFamilyUseAllocationTable } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTable";
import {
  FundFamilyUseAllocationTableGetErrorForCellFn,
  FundFamilyUseAllocationTableOnRowChange,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { decimalOrZero } from "shared/utils/DecimalUtils";

export interface FeePostApplyOffsetsTableProps {
  fundFamilyId: string;
  feeChargeEventId: string;
  feeChargeEvent: FeeChargeEventPageData;
  rows: FundFamilyUseAllocationTableRow[];
  onRowChange: FundFamilyUseAllocationTableOnRowChange;
  onClickAddOffset?: () => void;
  adjustmentEvent?: FundFamilyEventData; // defined only for late offset applications
  getErrorForCell?: FundFamilyUseAllocationTableGetErrorForCellFn;
}

export const FeePostApplyOffsetsTable: React.FC<
  FeePostApplyOffsetsTableProps
> = ({
  fundFamilyId,
  feeChargeEventId,
  feeChargeEvent,
  rows,
  onRowChange,
  onClickAddOffset,
  adjustmentEvent,
  getErrorForCell,
}) => {
  const { t } = useTranslation(["fees", "common"]);

  const { data: fundList } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    require_commitment: true,
  });

  const addOffsetButton = (
    <Button colorScheme="tertiary" size="xs" onClick={onClickAddOffset}>
      {t("fees:offsets.add.addOffset")}
    </Button>
  );

  const allocationDef = useMemo<FundFamilyUseAllocationTableDef>(
    () => ({
      column_data_type: "fee_offset",
      enable_row_selection: true,
      amount_types: [
        {
          id: "offset",
        },
      ],
      categories: [
        {
          id: "offset",
          allow_auto_allocate: true,
        },
      ],
      fund_family: {
        fund_family_id: fundFamilyId,
        title: "Fund Family (Total)",
      },
      funds: fundList.map((f) => ({
        fund_id: f.id,
        title: f.legal_name,
      })),
      headers: [
        {
          id: "transaction_code_name",
          title: t("fees:applyOffsets.feeCode"),
          width: 400,
        },
        {
          id: "amount_remaining",
          title: t("fees:applyOffsets.totalAmountRemaining"),
          type: "money",
        },
      ],
      trailers: [
        {
          id: "remainingAmount",
          title: t("fees:applyOffsets.remainingAmount"),
          type: "money",
        },
        {
          id: "amount_posted_last_quarter",
          title: t("fees:applyOffsets.amountPostedLastQuarter"),
          type: "money",
          width: 140,
        },
        {
          id: "notes",
          title: t("common:notes"),
          flex: 1,
          width: 200,
        },
      ],
    }),
    [fundFamilyId, fundList, t],
  );

  const getAllocationsByLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    rows.map((row) => ({
      investment_id: null,
      transaction_code_id:
        getFundAllocationColumnData(row)!.transaction_code_id,
      fund_ids: Object.keys(row.funds),
    })),
    adjustmentEvent?.effective_date ?? feeChargeEvent.transaction_date,
    undefined,
    feeChargeEventId,
  );

  const valueGetter = useCallback<
    ValueGetterFunc<FundFamilyUseAllocationTableRow>
  >((params) => {
    if (params.column.getColId() === "remainingAmount") {
      const amountRemaining = decimalOrZero(
        getFundAllocationColumnData(params.data)?.amount_remaining,
      );
      const fundFamilyAmount = decimalOrZero(
        params.data?.fund_family["offset"].amount,
      );
      const fundAmounts = Object.values(params.data?.funds ?? {}).map((f) =>
        decimalOrZero(f["offset"].amount),
      );
      const isProrata = Boolean(params.data?.is_auto_allocate);
      const total = isProrata
        ? amountRemaining.minus(fundFamilyAmount)
        : amountRemaining.minus(
            fundAmounts.reduce((a, b) => a.plus(b), new Decimal(0)),
          );
      return total.toString();
    }
  }, []);

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  return (
    <TableBox isFullWidth={true}>
      <TableBoxHeader>
        <TableBoxTitle>{t("fees:applyOffsets.applyOffsets")}</TableBoxTitle>
        <TableBoxActions>
          {rows.length ? addOffsetButton : null}
        </TableBoxActions>
      </TableBoxHeader>
      <TableBoxBody>
        <FundFamilyUseAllocationTable
          tableDef={allocationDef}
          tableData={{ rows }}
          onRowChange={onRowChange}
          context={{
            getAllocationsByLookupKey,
            roundingMethodology: fundFamilyConfig.config.rounding_methodology,
          }}
          noRowsOverlayComponentParams={{
            emptyStateProps: {
              title: t("fees:offsets.empty.title"),
              children: addOffsetButton,
            },
          }}
          getAllocation={getFundAllocationForFeeOffsets}
          valueGetter={valueGetter}
          getErrorForCell={getErrorForCell}
          sx={{
            ".ag-cell[col-id='transaction_code_name']": {
              textStyle: "bodyBold",
            },
          }}
        />
      </TableBoxBody>
    </TableBox>
  );
};
